import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ClipboardList, Wallet, User, LogOut, Menu, X, Handshake } from "lucide-react";

// Minimal portal for independent service providers: their work, their payouts,
// their profile. No dashboard, attendance, leave, policies or notifications —
// they are not employees.
const NAV = [
  { to: "/", label: "My Work", icon: ClipboardList, end: true },
  { to: "/payouts", label: "Payout Advices", icon: Wallet },
  { to: "/profile", label: "My Profile", icon: User },
];

export default function ProviderLayout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="App flex bg-background">
      <aside className={cn(
        "brand-sidebar fixed z-40 inset-y-0 left-0 w-64 flex flex-col transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <img src="/intenciv-mark.webp" alt="IntenCiv" className="h-9 w-9 rounded-md object-contain bg-white/95 p-0.5" />
          <div className="leading-tight">
            <p className="text-white font-serif-display text-lg tracking-tight">IntenCiv</p>
            <p className="text-teal text-[10px] tracking-[0.2em] uppercase">Service Provider</p>
          </div>
          <button className="ml-auto lg:hidden text-white/70" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              className={({ isActive }) => cn("nav-link relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/75", isActive && "nav-link-active")}>
              <n.icon className="h-5 w-5 shrink-0" strokeWidth={2} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-teal/20 grid place-items-center text-white text-sm font-semibold">{user?.name?.[0]?.toUpperCase() || "S"}</div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-[11px] flex items-center gap-1"><Handshake className="h-3 w-3" /> {user?.email?.toUpperCase()}</p>
            </div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/75 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-navy" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <img src="/intenciv-logo.webp" alt="IntenCiv Diagnostics" className="h-7 object-contain" />
          <span className="ml-auto overline text-slate-400 hidden sm:inline">Service Provider Portal</span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto fade-in">{children}</main>
      </div>
    </div>
  );
}
